import * as Dialog from '@radix-ui/react-dialog'
import { Cross2Icon, EyeOpenIcon } from '@radix-ui/react-icons'
import * as Tabs from '@radix-ui/react-tabs'
import { match } from 'ts-pattern'
import { mappedColors, typography } from '../../main.css.ts'
import { Routes } from '../../routes.ts'
import { useAnalysisDetailsPageUI } from '../../routes/analysis-details-page.tsx'
import { Finding } from '../../utils/api-client/api-schemas.ts'
import { Theme } from '../../utils/higher-order-components/with-theme.tsx'
import { SeverityBadge } from '../badges/severity-badge.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import { getSuggestedStatusLabel } from '../findings-table.tsx'
import { Markdown } from '../markdown.tsx'
import { Spinner } from '../spinner.tsx'
import * as styles from './finding-triage-drawer.css.ts'

export interface FindingTriageDrawerProps {
  analysisId: string
  findingId: string
  finding: Finding
  triageArticle?: string
  triageReport?: string
  theme: Theme
  handleClose: () => void
}

export const FindingTriageDrawer: React.FC<FindingTriageDrawerProps> = ({
  analysisId,
  findingId,
  finding,
  triageArticle,
  triageReport,
  theme,
  handleClose,
}) => {
  const selectedTab = useAnalysisDetailsPageUI(state => state.findingDetailsTab)
  const setSelectedTab = useAnalysisDetailsPageUI(state => state.setFindingDetailsTab)

  return (
    <Dialog.Root open={true} modal={false}>
      <Dialog.Content className={styles.content} aria-describedby={undefined}>
        <Dialog.Close asChild className={styles.closeButton}>
          <button aria-label="Close" onClick={handleClose}>
            <Cross2Icon className={styles.closeButtonIcon} />
          </button>
        </Dialog.Close>
        <FindingDetails finding={finding} />
        <Tabs.Root value={selectedTab} onValueChange={value => setSelectedTab(value as 'summary' | 'full-report')} className={styles.tabContainer}>
          <div style={{ position: 'relative' }}>
            <Tabs.List className={styles.tabList}>
              <Tabs.Trigger className={styles.tab} value="summary">
                SUMMARY
              </Tabs.Trigger>
              <Tabs.Trigger className={styles.tab} value="full-report">
                FULL REPORT
              </Tabs.Trigger>
            </Tabs.List>
            {finding._embedded?.fixes?.total > 0 && (
              <div style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)' }}>
                <GhostButton to={Routes.AnalysisDetailsPageFindingFixDrawer.createPath({ analysisId, findingId })}>
                  <EyeOpenIcon style={{ marginRight: '4px', color: mappedColors.neutral.white, width: '16px', height: '16px' }} />
                  <span className={typography.small}>Related fix</span>
                </GhostButton>
              </div>
            )}
          </div>

          <Tabs.Content className={styles.tabContent} value="summary">
            <TriageMetadata finding={finding} />
            <TriageArticle markdown={triageArticle} theme={theme} />
          </Tabs.Content>
          <Tabs.Content className={styles.tabContent} value="full-report">
            <TriageMetadata finding={finding} />
            <TriageReport markdown={triageReport} theme={theme} />
          </Tabs.Content>
        </Tabs.Root>
      </Dialog.Content>
    </Dialog.Root>
  )
}

const FindingDetails: React.FC<{
  finding: Finding
}> = ({ finding }) => {
  const getTriageResult = (finding: Finding) => {
    if (!finding._embedded?.triage) return 'Not Triaged'
    return match(finding._embedded.triage.result)
      .with('triaged', () => 'Triaged')
      .with('rule_not_supported', () => 'Rule Not Supported')
      .with('error', () => 'Error')
      .exhaustive()
  }

  return (
    <section className={styles.header} aria-label="Finding Details">
      <Dialog.Title className={styles.title}>{finding.title}</Dialog.Title>
      <div className={styles.metadata}>
        <div className={styles.metadataItem}>
          <label id={`rule-label-${finding.id}`} className={styles.metadataItemLabel}>
            Rule
          </label>
          <span className={styles.metadataItemValue} aria-labelledby={`rule-label-${finding.id}`}>
            {finding.rule}
          </span>
        </div>
        <div className={styles.metadataItem}>
          <label id={`finding-label-${finding.id}`} className={styles.metadataItemLabel}>
            Finding
          </label>
          <span className={styles.metadataItemValue} aria-labelledby={`finding-label-${finding.id}`}>
            {finding.id}
          </span>
        </div>
        <div className={styles.metadataItem}>
          <label id={`severity-label-${finding.id}`} className={styles.metadataItemLabel}>
            Severity
          </label>
          <span className={styles.metadataItemValue} aria-labelledby={`severity-label-${finding.id}`} style={{ textTransform: 'capitalize' }}>
            {finding.severity?.label}
          </span>
        </div>
        <div className={styles.metadataItem}>
          <label id={`result-label-${finding.id}`} className={styles.metadataItemLabel}>
            Result
          </label>
          <span className={styles.metadataItemValue} aria-labelledby={`result-label-${finding.id}`}>
            {getTriageResult(finding)}
          </span>
        </div>
      </div>
    </section>
  )
}

const TriageArticle: React.FC<{
  markdown?: string
  theme: Theme
}> = ({ markdown, theme }) => {
  if (markdown === undefined) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          paddingTop: '64px',
        }}
      >
        <Spinner label="Loading finding triage article" size="large" />
      </div>
    )
  }

  if (markdown === '') {
    return (
      <div className={styles.markdownContainer}>
        <div className={styles.noTriageText}>No triage summary found for this finding.</div>
      </div>
    )
  }

  return (
    <div className={styles.markdownContainer}>
      <Markdown theme={theme} markdown={reformatFindingTriageArticleMarkdown(markdown)} />
    </div>
  )
}

const TriageReport: React.FC<{
  markdown?: string
  theme: Theme
}> = ({ markdown, theme }) => {
  if (markdown === undefined) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', paddingTop: '64px' }}>
        <Spinner label="Loading finding triage report" size="large" />
      </div>
    )
  }

  if (markdown === '') {
    return (
      <div className={styles.markdownContainer}>
        <div className={styles.noTriageText}>No triage report found for this finding.</div>
      </div>
    )
  }

  return (
    <div className={styles.markdownContainer}>
      <Markdown theme={theme} markdown={markdown} />
    </div>
  )
}

const TriageMetadata: React.FC<{ finding: Finding }> = ({ finding }) => {
  if (finding._embedded?.triage?.result !== 'triaged') {
    return null
  }

  return (
    <div className={styles.triageMetadataContainer}>
      <div className={styles.suggestedChangesTitle}>Suggested Changes Based On Risk Factors:</div>
      {finding._embedded?.triage?.suggested_status && (
        <div className={styles.metadataItem}>
          <label id={`suggested-status-label-${finding.id}`} className={styles.suggestedChangeLabel}>
            STATUS
          </label>
          <span className={styles.suggestedChangeValue} aria-labelledby={`suggested-status-label-${finding.id}`}>
            {getSuggestedStatusLabel(finding._embedded?.triage?.suggested_status)}
          </span>
        </div>
      )}
      {finding._embedded?.triage?.suggested_severity && (
        <div className={styles.metadataItem}>
          <label id={`suggested-severity-label-${finding.id}`} className={styles.suggestedChangeLabel}>
            SEVERITY
          </label>
          <span
            className={styles.suggestedChangeValue}
            aria-labelledby={`suggested-severity-label-${finding.id}`}
            style={{ textTransform: 'capitalize' }}
          >
            <SeverityBadge severity={finding._embedded.triage.suggested_severity} />
          </span>
        </div>
      )}
    </div>
  )
}

export const reformatFindingTriageArticleMarkdown = (markdown: string) => {
  return markdown
    .replace(/<details>/g, '')
    .replace(/<\/details>/g, '')
    .replace(/\s*<i>Suggestion:.*?<\/i><br\/>\s*/g, '')
    .split('\n')
    .map(line => line.trim())
    .join('\n')
    .trim()
}
